/**
 * ArrayFkCell
 *
 * Renders a cell whose value is an array AND whose column has a loose FK
 * declared. Each array element becomes its own clickable pill that opens the
 * existing FK popover. A `</>` icon opens the standard expanded-value dialog
 * (full JSON view), and overflow (more than `visibleCount` elements) collapses
 * into a "+N more…" trigger that opens a small popover containing the rest of
 * the elements as the same clickable pills.
 *
 * Used by both Query Editor results grid (`useCellRenderer`) and Table
 * Explorer grid (`TableExplorerGrid` inline cell renderer).
 */

import { useMemo } from 'react';
import { Code2 } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '../../../../components/ui/popover';
import { LazyForeignKeyCell } from '../../query-editor/components/LazyForeignKeyCell';
import { useMetaInfo } from '../hooks/useMetaInfo';
import { evaluateFilterGroupsAgainstRow } from '../utils/filterEvaluator';

type Surface = 'query_editor' | 'table_explorer';

type ArrayFkCellProps = {
  elements: unknown[];
  visibleCount: number;
  connectionId: string;
  database: string;
  sourceSchema: string;
  sourceTable: string;
  sourceColumn: string;
  surface: Surface;
  /** Opens the existing expanded-value dialog with the full JSON of the array. */
  onOpenExpandedView: () => void;
  /** Full source row data — threaded to each LazyForeignKeyCell pill for filter-aware loose FK logic (Task 3.5). */
  sourceRow?: Record<string, unknown>;
};

function elementDisplay(el: unknown): string {
  return el === null || el === undefined ? 'NULL' : String(el);
}

function renderPill(
  el: unknown,
  idx: number,
  ctx: Pick<ArrayFkCellProps, 'connectionId' | 'database' | 'sourceSchema' | 'sourceTable' | 'sourceColumn' | 'surface' | 'sourceRow'>,
  isLoose: boolean,
) {
  return (
    <LazyForeignKeyCell
      key={idx}
      connectionId={ctx.connectionId}
      database={ctx.database}
      sourceSchema={ctx.sourceSchema}
      sourceTable={ctx.sourceTable}
      sourceColumn={ctx.sourceColumn}
      value={el}
      displayValue={elementDisplay(el)}
      isLoose={isLoose}
      surface={ctx.surface}
      sourceRow={ctx.sourceRow}
    />
  );
}

export function ArrayFkCell({
  elements,
  visibleCount,
  connectionId,
  database,
  sourceSchema,
  sourceTable,
  sourceColumn,
  surface,
  onOpenExpandedView,
  sourceRow,
}: ArrayFkCellProps) {
  const { data: metaInfo } = useMetaInfo(connectionId, database);

  // Multi-match loose FK lookup gated by source-row filter evaluation.
  // If sourceRow is not available, skip filter gating and return all matches.
  const matchingLooseRels = useMemo(() => {
    if (!sourceSchema || !sourceTable || !sourceColumn) return [];
    const all = (metaInfo?.looseRelationships ?? []).filter(
      (r) =>
        r.fromSchema === sourceSchema &&
        r.fromTable === sourceTable &&
        r.fromColumn === sourceColumn,
    );
    if (!sourceRow) return all;
    return all.filter((r) => evaluateFilterGroupsAgainstRow(sourceRow, r.sourceFilter));
  }, [metaInfo, sourceSchema, sourceTable, sourceColumn, sourceRow]);

  const isLoose = matchingLooseRels.length > 0;

  const visible = elements.slice(0, visibleCount);
  const hidden = elements.slice(visibleCount);
  const ctx = { connectionId, database, sourceSchema, sourceTable, sourceColumn, surface, sourceRow };

  // Single-row layout: `</>` pinned left, pills in a horizontally-clipped middle
  // band, `+N more…` pinned right. The pill band uses `min-w-0 overflow-hidden`
  // so it shrinks-to-fit within the cell width — same single-line truncation
  // behaviour large text columns get — without ever pushing the row taller.
  return (
    <span className="flex items-center gap-1 min-w-0 w-full overflow-hidden">
      <button
        type="button"
        className="inline-flex h-4 w-4 items-center justify-center rounded text-muted-foreground hover:text-foreground hover:bg-muted/60 flex-shrink-0"
        title="View JSON"
        aria-label="View JSON"
        onClick={(event) => {
          event.stopPropagation();
          onOpenExpandedView();
        }}
        onDoubleClick={(event) => event.stopPropagation()}
        data-testid="cell-expand-btn"
      >
        <Code2 className="h-3 w-3" />
      </button>
      <span className="flex items-center gap-1 min-w-0 overflow-hidden flex-1">
        {visible.map((el, idx) => renderPill(el, idx, ctx, isLoose))}
      </span>
      {hidden.length > 0 && (
        <Popover>
          <PopoverTrigger asChild>
            <button
              type="button"
              className="inline-flex items-center text-[10px] text-muted-foreground hover:text-foreground underline decoration-dotted flex-shrink-0"
              title={`Show ${hidden.length} more`}
              data-testid="array-fk-overflow-trigger"
              onClick={(event) => event.stopPropagation()}
            >
              +{hidden.length} more…
            </button>
          </PopoverTrigger>
          <PopoverContent
            className="w-auto max-w-md p-2"
            align="end"
            data-testid="array-fk-overflow-popover"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex flex-wrap gap-1">
              {hidden.map((el, idx) => renderPill(el, idx + visibleCount, ctx, isLoose))}
            </div>
          </PopoverContent>
        </Popover>
      )}
    </span>
  );
}
